import {getAllFocusableElements, usePopoverStack} from '@aboutyou/a11y';
import {usercentricsScriptId} from '@aboutyou/head-elements';
import {useIsConsentBannerClosed} from '@aboutyou/storage';
import {useEffect, useRef} from 'react';

const usercentricsId = `${usercentricsScriptId}-ui`;

export const ConsentBannerFocusManagement = () => {
  const isConsentBannerClosed = useIsConsentBannerClosed();
  const {isStackReady, toggleBlockStack} = usePopoverStack();
  const shouldFocusBodyOnClose = useRef(false);

  const focusOnFirstShadowRootElement = () => {
    const consentBanner = document.getElementById(usercentricsId);

    if (
      consentBanner &&
      !consentBanner.contains(document.activeElement) &&
      consentBanner.shadowRoot
    ) {
      const focusable = getAllFocusableElements(consentBanner.shadowRoot);
      focusable[0]?.focus();
    }
  };

  useEffect(() => {
    if (isConsentBannerClosed) {
      toggleBlockStack(false);
    } else {
      // This effect executes too soon, before the stack is ready, and we need to wait to block the popover stack
      if (!isStackReady) {
        return;
      }

      shouldFocusBodyOnClose.current = true;
      // we need to block the popover stack when the consent banner is present, since this will be the top most opened popover
      toggleBlockStack(true);
    }
  }, [isConsentBannerClosed, isStackReady]);

  // focus on first element in the web component once opened, and on the body, once closed
  useEffect(() => {
    if (isConsentBannerClosed) {
      if (shouldFocusBodyOnClose.current) {
        const bodyFocusableElements = getAllFocusableElements(document.body)[0];

        if (
          bodyFocusableElements &&
          bodyFocusableElements.id !== 'skip-to-main-content'
        ) {
          bodyFocusableElements.focus({preventScroll: true});
        }
      }
    } else {
      focusOnFirstShadowRootElement();
    }
  }, [isConsentBannerClosed]);

  useEffect(() => {
    if (isConsentBannerClosed) {
      return;
    }

    const handleOnKeyDown = (e: KeyboardEvent) => {
      const isTabPressed = e.key === 'Tab' || e.keyCode === 9;

      if (!isTabPressed) {
        return null;
      }

      focusOnFirstShadowRootElement();
    };

    document.addEventListener('keydown', handleOnKeyDown);

    return () => {
      document.removeEventListener('keydown', handleOnKeyDown);
    };
  }, [isConsentBannerClosed, shouldFocusBodyOnClose.current]);

  return null;
};
